import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import LikeIcon from '@/components/icons/LikeIcon';
import WhatsappIcon1 from '@/components/icons/WhatsappIcon1';
import { POST_TYPE } from '@/constants/posts';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { isArray } from '@/utils/helper';
import getWhatsappShareURL from '@/utils/helpers/getWhatsappShareURL';
import CommentIcon from '../icons/CommentIcon';
import InPageSpinner from '../InPageSpinner';
interface LikeButtonProps {
  liked?: boolean;
  onClick?: VoidFunction;
  loading?: boolean;
  tags: number[];
  postType?: string;
}
interface ShareBtnProps {
  slug: string;
  postType?: string;
  tags: number[];
}
interface CommentBtnProps {
  slug: string;
  postType?: string;
  onClick?: VoidFunction;
}
interface Props {
  liked?: boolean;
  onLikeClick: VoidFunction;
  onCommentClick?: VoidFunction;
  slug: string;
  loading?: boolean;
  postType?: string;
  tags: number[];
}
const ShareButton = ({
  slug,
  postType,
  tags
}: ShareBtnProps) => {
  const {
    t
  } = useTranslation('main');
  const [href, setHref] = useState('');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  useEffect(() => {
    setHref(location.href);
  }, []);
  const handleShareClick = () => {
    const eventType = postType === POST_TYPE.Community ? CustomEventTypes.COMMUNITY_POST_SHARED : CustomEventTypes.DEHAAT_FEED_SHARED;
    trackCustomEvent(eventType, isArray(tags) ? JSON.stringify(tags) : undefined);
  };
  return <a href={href ? getWhatsappShareURL(href, slug, postType) : undefined} className="flex items-center text-sm" target="_blank" rel="nofollow noreferrer" onClick={handleShareClick} data-sentry-component="ShareButton" data-sentry-source-file="LikeShareButtons.tsx">
      <WhatsappIcon1 className={`mr-2`} data-sentry-element="WhatsappIcon1" data-sentry-source-file="LikeShareButtons.tsx" />
      {t('share_btn')}
    </a>;
};
const LikeButton = ({
  liked,
  onClick,
  loading,
  tags,
  postType
}: LikeButtonProps) => {
  const {
    t
  } = useTranslation('main');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const handleLikeClick = () => {
    if (onClick) onClick();
    const eventType = postType === POST_TYPE.Community ? CustomEventTypes.LIKE_COMMUNITY_POST : CustomEventTypes.LIKE_DEHAAT_FEED;
    trackCustomEvent(eventType, isArray(tags) ? JSON.stringify(tags) : undefined);
  };
  return loading ? <button className="flex" data-sentry-component="LikeButton" data-sentry-source-file="LikeShareButtons.tsx">
      <InPageSpinner data-sentry-element="InPageSpinner" data-sentry-source-file="LikeShareButtons.tsx" />
    </button> : <button className="flex items-center text-sm" onClick={handleLikeClick} data-testid="like-button" data-sentry-component="LikeButton" data-sentry-source-file="LikeShareButtons.tsx">
      <LikeIcon className={`h-6 w-6 mr-2 stroke-[20px] ${liked ? 'fill-primary-100 stroke-primary-100' : 'stroke-black fill-white'}`} data-sentry-element="LikeIcon" data-sentry-source-file="LikeShareButtons.tsx" />
      {liked ? t('liked_btn') : t('like_btn')}
    </button>;
};
const CommentButton = ({
  onClick,
  slug,
  postType
}: CommentBtnProps) => {
  const {
    t
  } = useTranslation('posts');
  const {
    push
  } = useRouter();
  const [href, setHref] = useState('');
  useEffect(() => {
    setHref(location.href);
  }, []);
  const handleCommentsClick = () => {
    if (onClick) {
      onClick();
    }
    if (href.includes(slug)) {
      const commentBar = document.getElementById('comments-bar');
      commentBar?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    } else {
      //push router to spcific page
      push(`/${postType}/post/${slug}/#comment`);
    }
  };
  return <button className="flex items-center text-sm" onClick={handleCommentsClick} data-testid="comment-button" data-sentry-component="CommentButton" data-sentry-source-file="LikeShareButtons.tsx">
      <CommentIcon className="stroke-neutral-90 mr-2 w-6 h-6" data-sentry-element="CommentIcon" data-sentry-source-file="LikeShareButtons.tsx" />
      {t('total_comment_caption')}
    </button>;
};
const LikeShareButtons = ({
  liked,
  onLikeClick,
  onCommentClick,
  slug,
  loading,
  postType,
  tags
}: Props) => {
  return <div className="flex justify-between mt-4 py-4 border-t-[1px] border-t-neutral-20 border-b-[1px] border-b-neutral-20 border-solid" data-sentry-component="LikeShareButtons" data-sentry-source-file="LikeShareButtons.tsx">
      <LikeButton liked={liked} onClick={onLikeClick} loading={loading} tags={tags} postType={postType} data-sentry-element="LikeButton" data-sentry-source-file="LikeShareButtons.tsx" />
      <CommentButton slug={slug} postType={postType} onClick={onCommentClick} data-sentry-element="CommentButton" data-sentry-source-file="LikeShareButtons.tsx" />
      <ShareButton slug={slug} postType={postType} tags={tags} data-sentry-element="ShareButton" data-sentry-source-file="LikeShareButtons.tsx" />
    </div>;
};
export default LikeShareButtons;